import React, { useEffect, useRef } from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import { length, path} from 'ramda';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { withViewport } from '../../util/contextHelpers';
import { ensureTransaction, ensureUser, ensureListing } from '../../util/data';
import {
  getUserTxRole,
  isRelevantPastTransition,
  TRANSITION_COUNTER_PROPOSAL,
  TRANSITION_COUNTER_PROPOSAL_ACCEPT,
  TRANSITION_COUNTER_PROPOSAL_DECLINE,
  TRANSITION_PROPOSAL,
  TRANSITION_PROPOSAL_ACCEPT,
  TRANSITION_PROPOSAL_DECLINE,
  TRANSITION_REQUEST_PAYMENT_AFTER_COUNTER_PROPOSAL,
  TRANSITION_REQUEST_PAYMENT_AFTER_PROPOSAL,
  txIsEnquired,
  txRoleIsCustomer,
} from '../../util/transactions';
import { propTypes } from '../../util/types';
import {InlineTextButton} from '../../components';

import { isMessage, organizedItems } from './ActivityFeed.helpers';
import Message from './components/Message/Message';
import OwnMessage from './components/Message/OwnMessage';
import Transition, { EmptyTransition } from './components/Transition/Transition';
import Pending from './components/Pending/Pending';
import css from './ActivityFeed.module.css';
import Confirm from './components/Confirm/Confirm';
import Refusal from './components/Refusal/Refusal';
import RedirectToOfferPage from "./components/RedirectToOfferPage/RedirectToOfferPage";
import {formatMoney} from "../../util/currency";
import {types as sdkTypes} from "../../util/sdkLoader";
import {formatDate} from "../../util/dates";
import {getInitialValuesFromTransaction} from "../BookingPanel/BookingPanel.helpers";
import AskForProposal from "./components/AskForProposal/AskForProposal";

const MAX_MOBILE_WIDTH = 1279;
const {Money} = sdkTypes;

const ActivityFeed = (props) => {
  const {
    rootClassName,
    className,
    messages,
    transaction,
    currentUser,
    hasOlderMessages,
    onOpenReviewModal,
    onShowOlderMessages,
    fetchMessagesInProgress,
    intl,
    viewport,
  } = props;
  const ActivityFeedRef = useRef();
  const { width } = viewport;
  const hasViewport = width > 0;
  const isMobileDevice = hasViewport && width <= MAX_MOBILE_WIDTH;
  useEffect(() => {
    if (length(messages) <= 0) return;

    ActivityFeedRef.current.scrollIntoView();
  }, [messages]);
  const classes = classNames(rootClassName || css.root, className);
  const currentTransaction = ensureTransaction(transaction);
  const transitions = currentTransaction.attributes.transitions
    ? currentTransaction.attributes.transitions
    : [];
  const currentCustomer = ensureUser(currentTransaction.customer);
  const currentProvider = ensureUser(currentTransaction.provider);
  const currentListing = ensureListing(currentTransaction.listing);
  const isEnquire = transaction && txIsEnquired(transaction);
  const transitionsAvailable = !!(
    currentUser &&
    currentUser.id &&
    currentCustomer.id &&
    currentProvider.id &&
    currentListing.id
  );

  // combine messages and transaction transitions
  const items = organizedItems(messages, transitions, hasOlderMessages || fetchMessagesInProgress);

  const transitionComponent = (transition) => {
    if (transitionsAvailable) {
      return (
        <Transition
          transition={transition}
          transaction={transaction}
          currentUser={currentUser}
          intl={intl}
          onOpenReviewModal={onOpenReviewModal}
        />
      );
    } else {
      return <EmptyTransition />;
    }
  };

  const messageComponent = (message, prevMessageHasSameAuthor, nextMessageHasSameAuthor) => {
    const messageProps = {
      message,
      intl,
      isMobileDevice,
      nextMessageHasSameAuthor,
      prevMessageHasSameAuthor,
    };
    const getProposalDataFromMessage = (text) => {
      const pattern = /__proposal:({.*}):proposal__/;
      const match = pattern.exec(text);
      if (match && match[1]) {
        try {
          return JSON.parse(match[1]);
        } catch (error) {
          console.error("Erreur lors de l'analyse du JSON:", error);
          return null;
        }
      } else {
        console.error("Aucune correspondance trouvée dans le texte.");
        return null;
      }
    };

    const isProposalPattern = (input) => {
      const pattern = /__proposal:({.*}):proposal__/;
      return pattern.test(input);
    }

    const text = message?.attributes?.content;
    if (isCustomer && isProposalPattern(text)) {
      const proposal = getProposalDataFromMessage(text);
      return (
        <RedirectToOfferPage
          intl={intl}
          bookingParams={getInitialValuesFromTransaction(transaction)}
          listingId={currentListing?.id?.uuid}
          priceAmount={proposal?.price}
          providerDisplayName={path(['attributes', 'profile', 'displayName'], currentProvider)}
        />
      );
    }
    if (!isCustomer && isProposalPattern(text)) {
      const proposal = getProposalDataFromMessage(text);
      return (
        <div className={css.providerOfferProposalReplacementContainer}>
              <span className={css.messageDate}>
              {formatDate(intl, intl.formatMessage({id: 'ActivityFeed.today'}), message.attributes.createdAt)}
            </span>
          <p className={css.providerOfferProposalReplacement}>
            <FormattedMessage id="ActivityFeed.ProviderOfferProposalReplacementCurrentUser"
                              values={{
                                price: formatMoney(
                                  intl,
                                  new Money(proposal.price, "EUR"),
                                  true,
                                ),
                              }}/>
          </p>
        </div>
      );
    }

    const isOwnMessage =
      message.sender &&
      message.sender.id &&
      currentUser &&
      currentUser.id &&
      message.sender.id.uuid === currentUser.id.uuid;
    if (isOwnMessage) {
      return <OwnMessage {...messageProps} />;
    }
    return <Message {...messageProps} />;
  };

  const messageListItem = (message, index, array) => {
    const arrayLength = array.length;
    const hasPrevItem = index > 0;
    const hasNextItem = arrayLength - 1 > index;
    const prevItem = hasPrevItem && array[index - 1];
    const nextItem = hasNextItem && array[index + 1];
    const prevItemIsMessage = prevItem && isMessage(prevItem);
    const nextItemIsMessage = nextItem && isMessage(nextItem);
    const prevMessageHasSameAuthor =
      prevItemIsMessage && message.sender?.id.uuid === prevItem.sender?.id.uuid;
    const nextMessageHasSameAuthor =
      nextItemIsMessage && message.sender?.id.uuid === nextItem.sender?.id.uuid;
    const messageItemClasses = classNames(css.messageItem, {
      [css.messageItemSameAuthor]: nextMessageHasSameAuthor,
    });

    return (
      <li id={`msg-${message.id.uuid}`} key={message.id.uuid} className={messageItemClasses}>
        {messageComponent(message, prevMessageHasSameAuthor, nextMessageHasSameAuthor)}
      </li>
    );
  };

  // TODO : do not apply transitionItem css in the case of some transitions
  const transitionListItem = (transition) => {
    if (transition && isRelevantPastTransition(transition.transition)) {
      return (
        <li key={transition.transition} className={css.transitionItem}>
          {transitionComponent(transition)}
        </li>
      );
    } else {
      return null;
    }
  };

  const ownRole = getUserTxRole(currentUser.id, currentTransaction);
  const isCustomer = txRoleIsCustomer(ownRole);
  const lastTransition = transitions.length > 0 && transitions[transitions.length - 1];

  const isPendingProposalAnswerFromProvider =
    !isCustomer &&
    transitionsAvailable &&
    lastTransition &&
    lastTransition.transition === TRANSITION_PROPOSAL;

  const isPendingCounterProposalAnswerFromCustomer =
    isCustomer &&
    transitionsAvailable &&
    lastTransition &&
    lastTransition.transition === TRANSITION_COUNTER_PROPOSAL;

  const isPendingCustomerPaymentRequestOrConfirmation =
    isCustomer &&
    transitionsAvailable &&
    lastTransition &&
    [
      TRANSITION_PROPOSAL_ACCEPT,
      TRANSITION_COUNTER_PROPOSAL_ACCEPT,
      TRANSITION_REQUEST_PAYMENT_AFTER_PROPOSAL,
      TRANSITION_REQUEST_PAYMENT_AFTER_COUNTER_PROPOSAL,
    ].includes(lastTransition.transition);

  const isProposalRefusal =
    isCustomer &&
    lastTransition &&
    [TRANSITION_PROPOSAL_DECLINE, TRANSITION_COUNTER_PROPOSAL_DECLINE].includes(
      lastTransition.transition
    );

  const getPendingComponent = () => {
    if (isPendingProposalAnswerFromProvider || isPendingCounterProposalAnswerFromCustomer) {
      return (
        <li key={lastTransition.transition} className={css.pendingItem}>
          <Pending
            transition={lastTransition}
            transaction={transaction}
            currentUser={currentUser}
            intl={intl}
            onOpenReviewModal={onOpenReviewModal}
          />
        </li>
      );
    }

    if (isPendingCustomerPaymentRequestOrConfirmation) {
      return (
        <li key={lastTransition.transition}>
          <Confirm
            transition={lastTransition}
            transaction={transaction}
            currentUser={currentUser}
            intl={intl}
          />
        </li>
      );
    }

    if (isProposalRefusal) {
      return (
        <li key={lastTransition.transition}>
          <Refusal
            transaction={transaction}
            currentUser={currentUser}
            transition={lastTransition}
            intl={intl}
          />
        </li>
      );
    }

    return null;
  };

  return (
    <ul className={classes}>
      {hasOlderMessages ? (
        <li className={css.showOlderWrapper} key="show-older-messages">
          <InlineTextButton className={css.showOlderButton} onClick={onShowOlderMessages}>
            <FormattedMessage id="ActivityFeed.showOlderMessages" />
          </InlineTextButton>
        </li>
      ) : null}
      {items.map((item, index, array) => {
        if (isMessage(item)) {
          return messageListItem(item, index, array);
        } else {
          return transitionListItem(item);
        }
      })}
      {getPendingComponent()}
      {isEnquire && !isCustomer && (
        <AskForProposal intl={intl} transactionId={transaction.id}/>
      )}
      {/* TODO: implement a successOrFailureComponent */}
      <div ref={ActivityFeedRef} />
    </ul>
  );
};

ActivityFeed.defaultProps = {
  rootClassName: null,
  className: null,
};

ActivityFeed.propTypes = {
  rootClassName: string,
  className: string,

  currentUser: propTypes.currentUser,
  transaction: propTypes.transaction,
  messages: arrayOf(propTypes.message),
  hasOlderMessages: bool.isRequired,
  onOpenReviewModal: func.isRequired,
  onShowOlderMessages: func.isRequired,
  fetchMessagesInProgress: bool.isRequired,
  intl: intlShape.isRequired,
};

export default withViewport(injectIntl(ActivityFeed));
